import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Modal, Tooltip, message } from 'antd'
import {
  CaretRightOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RedoOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import sensors from '../../../../../utils/sensors'
import classnames from 'classnames'
import styles from './index.module.css'

interface Photo {
  id: string
  smallURL: string
  largeURL: string
  videoURL?: string
  mediaType: 0 | 1
  time: number
  addr?: string
  watermarkName?: string
  photoNumber?: string
  sourceType?: number
}

interface Props {
  photos: Photo[]
  total?: number
  currentIndex: number
  isPersonalVip: boolean
  onClose: () => void
  onIndexChange: (index: number) => void
  onDelete: (id: string) => void
  onDownload: (id: string) => void
}

function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => message.success('已复制'))
  } else {
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    message.success('已复制')
  }
}

function formatTime(ts: number) {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const PhotoPreview: React.FC<Props> = ({
  photos,
  total: totalProp,
  currentIndex,
  isPersonalVip,
  onClose,
  onIndexChange,
  onDelete,
  onDownload
}) => {
  const displayTotal = totalProp ?? photos.length
  const [rotation, setRotation] = useState(0)
  const [scale, setScale] = useState(100)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [isHideInfo, setIsHideInfo] = useState(true)

  const photo = photos[currentIndex]

  const trackPreviewClick = useCallback(
    (clickItem: string, extra?: Record<string, any>) => {
      const cur = photos[currentIndex]
      if (!cur) return
      sensors.track('other_b7775_click', {
        clickItem,
        fileType: cur.mediaType === 1 ? 'video' : 'photo',
        sourceType: cur.sourceType === 1 ? 'todayCam' : 'notTodayCam',
        imageUrl: cur.largeURL || '',
        isPersonalVip,
        track_sign: 'aother.b7775.click.other_b7775_click',
        requirement_id: 608,
        ...extra
      })
    },
    [photos, currentIndex, isPersonalVip]
  )

  // 滚轮埋点节流
  const wheelThrottleRef = useRef(0)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (photo?.mediaType !== 0) return
      const now = Date.now()
      if (now - wheelThrottleRef.current < 500) return
      wheelThrottleRef.current = now
      if (e.deltaY < 0 && scale < 300) {
        trackPreviewClick('zoomInRoll')
        setScale((s) => Math.min(300, s + 25))
      } else if (e.deltaY > 0 && scale > 50) {
        trackPreviewClick('zoomOutRoll')
        setScale((s) => Math.max(50, s - 25))
      }
    },
    [photo?.mediaType, scale, trackPreviewClick]
  )

  const resetTransform = useCallback(() => {
    setRotation(0)
    setScale(100)
  }, [])

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1)
      resetTransform()
    }
  }, [currentIndex, onIndexChange, resetTransform])

  const goNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      onIndexChange(currentIndex + 1)
      resetTransform()
    }
  }, [currentIndex, photos.length, onIndexChange, resetTransform])

  // 切换照片时自动将缩略图滚动到可见区域
  useEffect(() => {
    setTimeout(() => {
      const frames = document.getElementsByClassName('ps-photo-frame')
      if (frames[currentIndex]) {
        frames[currentIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }, 0)
  }, [currentIndex])

  // 键盘导航
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        trackPreviewClick('previousKeyboard')
        goPrev()
      } else if (e.key === 'ArrowRight') {
        trackPreviewClick('nextKeyboard')
        goNext()
      } else if (e.key === 'Escape') {
        trackPreviewClick('closeKeyboard')
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [goPrev, goNext, onClose])

  const handleDelete = useCallback(() => {
    Modal.confirm({
      title: '是否删除该照片/视频？',
      content: '删除后不支持找回，请谨慎操作。',
      okText: '删除',
      cancelText: '取消',
      okType: 'danger',
      centered: true,
      onOk: () => onDelete(photo.id)
    })
  }, [photo, onDelete])

  const handleGalleryClick = useCallback(
    (index: number) => {
      if (index !== currentIndex) {
        onIndexChange(index)
        resetTransform()
      }
    },
    [currentIndex, onIndexChange, resetTransform]
  )

  if (!photo) return null

  const mediaStyle: React.CSSProperties = {
    transform: `rotate(${rotation}deg) scale(${scale / 100})`,
    transition: 'transform 0.3s'
  }

  return (
    <div className={styles.overlay}>
      {/* 主体区域 */}
      <div className={styles.viewerBody}>
        {/* 左侧查看器面板 */}
        <div className={styles.viewerPanel}>
          {/* 顶部导航栏（在 viewerPanel 内，宽度跟随 viewerPanel 收缩） */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <span className={styles.counter}>
                {currentIndex + 1} / {displayTotal}
              </span>
              {photo.photoNumber && (
                <span className={styles.photoNum}>
                  编号：{photo.photoNumber}
                </span>
              )}
            </div>
            <Tooltip title="关闭（ESC）">
              <span
                className={styles.closeBtn}
                onClick={() => {
                  trackPreviewClick('close')
                  onClose()
                }}
              >
                <CloseCircleOutlined />
                <span className={styles.closeText}>关闭</span>
              </span>
            </Tooltip>
          </div>

          {/* 照片内容区 */}
          <div
            className={classnames(styles.content, {
              [styles.contentFullscreen]: isFullScreen
            })}
          >
            {/* 左导航箭头 */}
            {currentIndex > 0 && (
              <span
                className={classnames(styles.navArrow, styles.navLeft)}
                onClick={() => {
                  trackPreviewClick('previous')
                  goPrev()
                }}
              >
                <LeftOutlined />
              </span>
            )}

            {/* 媒体内容 */}
            <div className={styles.mediaBox} onWheel={handleWheel}>
              {photo.mediaType === 1 && photo.videoURL ? (
                <video
                  key={photo.id}
                  src={photo.videoURL}
                  controls
                  className={styles.media}
                  style={mediaStyle}
                />
              ) : (
                <img
                  key={photo.id}
                  src={photo.largeURL}
                  alt=""
                  className={styles.media}
                  style={mediaStyle}
                  draggable={false}
                />
              )}
            </div>

            {/* 右导航箭头 */}
            {currentIndex < photos.length - 1 && (
              <span
                className={classnames(styles.navArrow, styles.navRight)}
                onClick={() => {
                  trackPreviewClick('next')
                  goNext()
                }}
              >
                <RightOutlined />
              </span>
            )}

            {/* 操作工具栏 — 悬浮在内容区底部居中，参考 ViewerV2/ActionBar */}
            <div className={styles.actions}>
              {/* 全屏切换 */}
              <Tooltip title={isFullScreen ? '退出全屏' : '全屏'}>
                <span
                  className={styles.actionItem}
                  onClick={() => {
                    trackPreviewClick('fullScreen')
                    setIsFullScreen((f) => !f)
                  }}
                >
                  {isFullScreen ? (
                    <FullscreenExitOutlined />
                  ) : (
                    <FullscreenOutlined />
                  )}
                </span>
              </Tooltip>

              {/* 旋转（仅图片） */}
              {photo.mediaType === 0 && (
                <Tooltip title="旋转">
                  <span
                    className={styles.actionItem}
                    onClick={() => {
                      trackPreviewClick('rotate')
                      setRotation((r) => r + 90)
                    }}
                  >
                    <RedoOutlined />
                  </span>
                </Tooltip>
              )}

              {/* 缩放（仅图片） */}
              {photo.mediaType === 0 && (
                <>
                  <Tooltip title="缩小">
                    <span
                      className={classnames(styles.actionItem, {
                        [styles.disabled]: scale <= 50
                      })}
                      onClick={() => {
                        if (scale > 50) {
                          trackPreviewClick('zoomOut')
                          setScale((s) => Math.max(50, s - 25))
                        }
                      }}
                    >
                      <ZoomOutOutlined />
                    </span>
                  </Tooltip>
                  <Tooltip title="放大">
                    <span
                      className={classnames(styles.actionItem, {
                        [styles.disabled]: scale >= 300
                      })}
                      onClick={() => {
                        if (scale < 300) {
                          trackPreviewClick('zoomIn')
                          setScale((s) => Math.min(300, s + 25))
                        }
                      }}
                    >
                      <ZoomInOutlined />
                    </span>
                  </Tooltip>
                </>
              )}

              {/* 信息面板 */}
              <Tooltip title={isHideInfo ? '查看水印信息' : '收起信息'}>
                <span
                  className={classnames(styles.actionItem, {
                    [styles.actionItemActive]: !isHideInfo
                  })}
                  onClick={() => setIsHideInfo((h) => !h)}
                >
                  <InfoCircleOutlined />
                </span>
              </Tooltip>

              {/* 分割线 */}
              <div className={styles.divider} />

              {/* 删除 */}
              <Tooltip title="删除">
                <span
                  className={styles.actionItem}
                  onClick={() => {
                    trackPreviewClick('delete')
                    handleDelete()
                  }}
                >
                  <DeleteOutlined />
                </span>
              </Tooltip>

              {/* 下载 */}
              <Tooltip title="下载">
                <span
                  className={styles.actionItem}
                  onClick={() => {
                    trackPreviewClick('download')
                    onDownload(photo.id)
                  }}
                >
                  <DownloadOutlined />
                </span>
              </Tooltip>
            </div>
          </div>

          {/* 底部缩略图栏 — 参考 ViewerV2/PhotoGallery，全屏时隐藏 */}
          {!isFullScreen && (
            <div className={styles.gallery}>
              <div className={styles.galleryCenterContainer}>
                <div className={styles.galleryWall}>
                  {photos.map((p, index) => (
                    <div
                      key={p.id}
                      className={classnames(
                        'ps-photo-frame',
                        styles.photoFrame,
                        { [styles.photoFrameActive]: index === currentIndex }
                      )}
                      onClick={() => {
                        trackPreviewClick('smallPic')
                        handleGalleryClick(index)
                      }}
                    >
                      <img src={p.smallURL} alt="" />
                      {p.mediaType === 1 && (
                        <div className={styles.playIcon}>
                          <CaretRightOutlined />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 右侧信息面板（对齐 ViewerV2/InfoPanel 布局：白底，title:content 行） */}
        {!isHideInfo && (
          <div className={styles.infoPanel}>
            <div className={styles.infoPanelBody}>
              <h4 className={styles.infoSectionTitle}>水印信息</h4>
              {[
                { label: '拍摄时间', value: formatTime(photo.time) },
                { label: '拍摄地点', value: photo.addr },
                { label: '水印名称', value: photo.watermarkName },
                { label: '照片编号', value: photo.photoNumber }
              ]
                .filter((item) => item.value)
                .map((item) => (
                  <div key={item.label} className={styles.wmItem}>
                    <div className={styles.wmTitle}>{item.label}</div>
                    <div
                      className={styles.wmContent}
                      onClick={() => {
                        trackPreviewClick('copy', {
                          title: item.label,
                          content: item.value
                        })
                        copyText(item.value!)
                      }}
                    >
                      <span>{item.value}</span>
                      <CopyOutlined className={styles.copyIcon} />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PhotoPreview
